import { forwardRef, ReactNode } from 'react';
import { DefaultProps, Selectors, Stack, Group } from '@mantine/core';
import { TextVariant } from '@ui/tokens/typography/typography';
import Icon, { IconProps } from '@ui/components/Icon/Icon';
import Text from '@ui/components/Text/Text';

import useStyles, { CalloutStylesParams } from './Callout.styles';
import { CalloutType, CalloutVariant, isSubtle } from './types';

type CalloutStylesNames = Selectors<typeof useStyles>;

export interface CalloutProps extends DefaultProps<CalloutStylesNames, CalloutStylesParams> {
	text: string | ReactNode;
	title?: string;
	icon?: IconProps['icon'];
	type: CalloutType;
	variant?: CalloutVariant;
	withIcon?: boolean;
	iconSize?: IconProps['size'];
}

const textVariant = ( title: boolean, variant: CalloutVariant ): TextVariant => {
	if ( isSubtle( variant ) ) { return 'caption-regular'; }

	return title ? 'footnote' : 'body';
}

const Callout = forwardRef<HTMLDivElement, CalloutProps>( ( {
	title,
	text,
	type,
	classNames,
	styles,
	unstyled,
	className,
	variant = 'outlined',
	withIcon = true,
	icon = 'info_outlined',
	iconSize = '16px',
	...others
}, ref ) => {
	const { classes, cx } = useStyles(
		{ type, variant },
		{ name: 'Callout', classNames, styles, unstyled }
	);

	return (
		<Group
			ref={ref}
			className={cx( classes.root, className )}
			spacing={8}
			noWrap
			align="flex-start"
			py={title ? 8 : 12}
			px={8}
			{...others}
		>
			{withIcon && <Icon icon={icon} className={classes.icon} size={iconSize} />}
			<Stack spacing={4}>
				{title && !isSubtle( variant ) && (
					<Text className={classes.title} variant="body-bold">{title}</Text>
				)}
				<Text className={classes.text} variant={textVariant( !!title, variant )}>{text}</Text>
			</Stack>
		</Group>
	);
} );

export default Callout;
